/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-pascal-case */
import { classNames } from '@commenda/utils';
import React from 'react';
import {
  ContextMenu as ContextMenuRoot,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from './_base';

interface IconProps {
  className: string;
}

type IconComponent = React.ComponentType<IconProps>;

export interface ContextMenuItemProps {
  icon?: IconComponent;
  label: string;
  shortcut?: string | React.ReactNode;
  disabled?: boolean;
  subMenu?: ContextMenuMenuProps;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  iconClassName?: string;
  labelClassName?: string;
  className?: string;
}

interface ContextMenuGroupProps {
  label?: string;
  items: ContextMenuItemProps[];
}

interface ContextMenuMenuProps {
  groups: ContextMenuGroupProps[];
}

export interface ContextMenuProps {
  menu: ContextMenuMenuProps;
  children: React.ReactNode;
  contentClassName?: string;
  itemClassName?: string;
  labelClassName?: string;
  /** className applied to the header label shown above each labeled group. */
  groupLabelClassName?: string;
  /**
   * When true, the automatic separators that appear after labeled groups and
   * between adjacent groups are suppressed.
   */
  hideSeparators?: boolean;
  /**
   * Notifies callers when the menu opens / closes — lets the parent highlight
   * the trigger row while the menu is open, etc.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Forwarded to `ContextMenu.Trigger`. When true (default), the trigger
   * projects onto the single child via Radix Slot rather than wrapping it in
   * an extra span — preserves the layout of the wrapped element.
   */
  asChild?: boolean;
  /** Disables right-click opening of the menu. */
  triggerDisabled?: boolean;
}

const ContextMenu = ({
  menu,
  children,
  contentClassName = '',
  itemClassName = '',
  labelClassName = '',
  groupLabelClassName = '',
  hideSeparators = false,
  onOpenChange,
  asChild = true,
  triggerDisabled = false,
}: ContextMenuProps) => {
  const handleItemClick = (
    event: React.MouseEvent<HTMLDivElement>,
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  ) => {
    event.stopPropagation();
    onClick?.(event);
  };

  const renderSubMenu = (item: ContextMenuItemProps) => {
    return (
      <ContextMenuSub>
        <ContextMenuSubTrigger>
          {item.icon && (
            <item.icon className="mr-2 h-4 w-4 text-fg-quaternary" />
          )}
          <span>{item.label}</span>
        </ContextMenuSubTrigger>
        <ContextMenuPortal>
          <ContextMenuSubContent>
            {item.subMenu?.groups.map((group, groupIndex) => (
              <React.Fragment key={groupIndex}>
                {group.label && (
                  <ContextMenuLabel>{group.label}</ContextMenuLabel>
                )}
                {group.label && <ContextMenuSeparator />}
                <ContextMenuGroup>
                  {group.items.map((_item, itemIndex) => {
                    return _item.subMenu ? (
                      renderSubMenu(_item)
                    ) : (
                      <ContextMenuItem
                        key={itemIndex}
                        disabled={_item.disabled}
                        onClick={(event) =>
                          handleItemClick(event, _item.onClick)
                        }
                      >
                        {_item.icon && (
                          <_item.icon
                            className={classNames(
                              'mr-2 h-4 w-4',
                              _item.iconClassName || ''
                            )}
                          />
                        )}
                        <span>{_item.label}</span>
                        {_item.shortcut &&
                          (typeof _item.shortcut === 'string' ? (
                            <ContextMenuShortcut>
                              {_item.shortcut}
                            </ContextMenuShortcut>
                          ) : (
                            _item.shortcut
                          ))}
                      </ContextMenuItem>
                    );
                  })}
                </ContextMenuGroup>
                {groupIndex < (item.subMenu?.groups.length || 0) - 1 && (
                  <ContextMenuSeparator />
                )}
              </React.Fragment>
            ))}
          </ContextMenuSubContent>
        </ContextMenuPortal>
      </ContextMenuSub>
    );
  };

  return (
    <ContextMenuRoot onOpenChange={onOpenChange}>
      <ContextMenuTrigger asChild={asChild} disabled={triggerDisabled}>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent
        className={classNames('w-56', contentClassName)}
      >
        {menu.groups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {group.label && (
              <ContextMenuLabel className={groupLabelClassName}>
                {group.label}
              </ContextMenuLabel>
            )}
            {group.label && !hideSeparators && <ContextMenuSeparator />}
            <ContextMenuGroup>
              {group.items.map((item, itemIndex) => {
                return item.subMenu ? (
                  renderSubMenu(item)
                ) : (
                  <ContextMenuItem
                    key={itemIndex}
                    disabled={item.disabled}
                    onClick={(event) => handleItemClick(event, item.onClick)}
                    className={classNames(itemClassName, item.className || '')}
                  >
                    <div className="flex items-center flex-1">
                      {item.icon && (
                        <item.icon
                          className={classNames(
                            'mr-2 h-4 w-4 shrink-0',
                            item.iconClassName || ''
                          )}
                        />
                      )}
                      <span
                        className={classNames(
                          labelClassName,
                          item.labelClassName || ''
                        )}
                      >
                        {item.label}
                      </span>
                    </div>
                    <div>
                      {item.shortcut &&
                        (typeof item.shortcut === 'string' ? (
                          <ContextMenuShortcut>
                            {item.shortcut}
                          </ContextMenuShortcut>
                        ) : (
                          item.shortcut
                        ))}
                    </div>
                  </ContextMenuItem>
                );
              })}
            </ContextMenuGroup>
            {groupIndex < menu.groups.length - 1 && !hideSeparators && (
              <ContextMenuSeparator />
            )}
          </React.Fragment>
        ))}
      </ContextMenuContent>
    </ContextMenuRoot>
  );
};

export { ContextMenu };
export type { ContextMenuMenuProps };
